import type { PermissionCode } from '@ldembalagens/types';
import { useQuery } from '@tanstack/react-query';
import {
  CornerDownLeft,
  Loader2,
  Package,
  Plus,
  Search,
  ShoppingCart,
  Store,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { customersApi, suppliersApi } from '@/features/customers/parties-api';
import { productsApi } from '@/features/products/products-api';
import { salesOrdersApi } from '@/features/sales/sales-api';
import { usePermissions } from '@/hooks/use-auth';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { useVisibleModules } from '@/hooks/use-visible-modules';
import { cn } from '@/lib/utils';

interface SearchResult {
  id: string;
  group: string;
  label: string;
  detail?: string;
  to: string;
  icon: LucideIcon;
}

const QUICK_ACTIONS: { label: string; to: string; permission: PermissionCode }[] = [
  { label: 'Novo produto', to: '/produtos/novo', permission: 'products:create' },
  { label: 'Novo cliente', to: '/clientes/novo', permission: 'customers:create' },
  { label: 'Novo fornecedor', to: '/fornecedores/novo', permission: 'suppliers:create' },
  { label: 'Novo orçamento', to: '/vendas/orcamentos/novo', permission: 'sales:create' },
  { label: 'Novo pedido de venda', to: '/vendas/pedidos/novo', permission: 'sales:create' },
  {
    label: 'Nova solicitação de compra',
    to: '/compras/pedidos/novo',
    permission: 'purchases:create',
  },
  { label: 'Nova ordem de produção', to: '/producao/ordens/nova', permission: 'production:create' },
  { label: 'Novo material', to: '/estoque/materiais/novo', permission: 'materials:create' },
  { label: 'Nova nota fiscal', to: '/notas-fiscais/nova', permission: 'invoices:create' },
  { label: 'Novo MTR', to: '/mtr/novo', permission: 'waste:create' },
];

/** Minúsculas e sem acentos, para comparar "orcamento" com "Orçamento". */
function normalize(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Busca global: páginas e seções dos módulos, ações de criação e, a partir de 2 caracteres,
 * produtos, clientes, fornecedores e pedidos de venda (respeitando as permissões).
 */
export function GlobalSearch() {
  const [open, setOpen] = useState(false);

  // Atalho ⌘K / Ctrl+K em qualquer tela
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden h-9 w-full max-w-md min-w-0 items-center gap-2 rounded-lg border border-border bg-subtle px-3 text-sm text-muted-foreground transition-colors outline-none hover:border-border-strong hover:bg-card focus-visible:ring-[3px] focus-visible:ring-primary/25 md:flex"
        aria-label="Busca global"
        aria-keyshortcuts="Meta+K Control+K"
      >
        <Search className="size-4 shrink-0" aria-hidden />
        <span className="truncate">Buscar páginas, produtos, clientes…</span>
        <kbd className="ml-auto hidden rounded border bg-card px-1.5 font-sans text-[11px] font-medium lg:inline">
          ⌘K
        </kbd>
      </button>
      <Button
        variant="ghost"
        size="icon"
        className="size-10 md:hidden"
        onClick={() => setOpen(true)}
        aria-label="Busca global"
      >
        <Search className="size-5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton={false}
          className="gap-0 overflow-hidden p-0 sm:top-[12vh] sm:max-w-xl sm:translate-y-0 sm:p-0 max-sm:px-0 max-sm:pt-0"
        >
          <DialogTitle className="sr-only">Busca global</DialogTitle>
          <DialogDescription className="sr-only">
            Digite para buscar páginas, ações e registros. Use as setas para navegar e Enter para
            abrir.
          </DialogDescription>
          {open ? <SearchPanel onClose={() => setOpen(false)} /> : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function SearchPanel({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const modules = useVisibleModules();
  const [term, setTerm] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const debounced = useDebouncedValue(term.trim(), 250);
  const remote = debounced.length >= 2;
  const q = normalize(term.trim());

  const products = useQuery({
    queryKey: ['global-search', 'products', debounced],
    queryFn: () => productsApi.list({ search: debounced, pageSize: 5 }),
    enabled: remote && can('products:read'),
  });
  const customers = useQuery({
    queryKey: ['global-search', 'customers', debounced],
    queryFn: () => customersApi.list({ search: debounced, pageSize: 5 }),
    enabled: remote && can('customers:read'),
  });
  const suppliers = useQuery({
    queryKey: ['global-search', 'suppliers', debounced],
    queryFn: () => suppliersApi.list({ search: debounced, pageSize: 5 }),
    enabled: remote && can('suppliers:read'),
  });
  const orders = useQuery({
    queryKey: ['global-search', 'orders', debounced],
    queryFn: () => salesOrdersApi.list({ search: debounced, pageSize: 5 }),
    enabled: remote && can('sales:read'),
  });
  const loading = [products, customers, suppliers, orders].some((r) => r.isFetching);

  const results = useMemo<SearchResult[]>(() => {
    const pages: SearchResult[] = modules.flatMap((m) => {
      const own = { id: m.to, group: 'Páginas', label: m.label, to: m.to, icon: m.icon };
      const children = (m.children ?? [])
        .filter((c) => !c.permissions || can(...c.permissions))
        .filter((c) => c.to !== m.to)
        .map((c) => ({
          id: c.to,
          group: 'Páginas',
          label: c.label,
          detail: m.label,
          to: c.to,
          icon: m.icon,
        }));
      return [own, ...children];
    });
    const actions: SearchResult[] = QUICK_ACTIONS.filter((a) => can(a.permission)).map((a) => ({
      id: `action:${a.to}`,
      group: 'Ações rápidas',
      label: a.label,
      to: a.to,
      icon: Plus,
    }));
    const match = (r: SearchResult) => !q || normalize(`${r.label} ${r.detail ?? ''}`).includes(q);
    const local = [
      ...pages.filter(match).slice(0, q ? 8 : 6),
      ...actions.filter(match).slice(0, 5),
    ];
    if (!remote) return local;
    return [
      ...local,
      ...(products.data?.data ?? []).map((p) => ({
        id: `product:${p.id}`,
        group: 'Produtos',
        label: p.name,
        detail: p.code,
        to: `/produtos/${p.id}`,
        icon: Package,
      })),
      ...(customers.data?.data ?? []).map((c) => ({
        id: `customer:${c.id}`,
        group: 'Clientes',
        label: c.tradeName || c.legalName,
        detail: [c.code, c.city].filter(Boolean).join(' · '),
        to: `/clientes/${c.id}`,
        icon: Users,
      })),
      ...(suppliers.data?.data ?? []).map((c) => ({
        id: `supplier:${c.id}`,
        group: 'Fornecedores',
        label: c.tradeName || c.legalName,
        detail: [c.code, c.city].filter(Boolean).join(' · '),
        to: `/fornecedores/${c.id}`,
        icon: Store,
      })),
      ...(orders.data?.data ?? []).map((o) => ({
        id: `order:${o.id}`,
        group: 'Pedidos de venda',
        label: `Pedido ${o.number}`,
        detail: o.customerName,
        to: `/vendas/pedidos/${o.id}`,
        icon: ShoppingCart,
      })),
    ];
  }, [modules, can, q, remote, products.data, customers.data, suppliers.data, orders.data]);

  const current = Math.min(active, Math.max(results.length - 1, 0));
  const go = (r: SearchResult | undefined) => {
    if (!r) return;
    onClose();
    void navigate(r.to);
  };

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${current}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [current]);

  let lastGroup = '';
  return (
    <div className="flex h-full min-h-0 flex-col max-sm:h-dvh">
      <div className="flex items-center gap-2 border-b px-3">
        <Search className="size-[18px] shrink-0 text-muted-foreground" aria-hidden />
        <input
          autoFocus
          role="combobox"
          aria-expanded="true"
          aria-controls="global-search-list"
          aria-activedescendant={results[current] ? `gs-${current}` : undefined}
          aria-label="Buscar"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((i) => Math.min(i + 1, results.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              go(results[current]);
            }
          }}
          placeholder="Buscar páginas, produtos, clientes, pedidos…"
          className="h-14 min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground md:h-12 md:text-sm"
        />
        {loading ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
        <Button variant="ghost" size="sm" onClick={onClose} className="sm:hidden">
          Fechar
        </Button>
        <kbd className="hidden rounded border bg-subtle px-1.5 text-[11px] text-muted-foreground sm:inline">
          Esc
        </kbd>
      </div>
      <ul
        ref={listRef}
        id="global-search-list"
        role="listbox"
        aria-label="Resultados"
        className="scrollbar-thin max-h-[min(60vh,28rem)] overflow-y-auto p-2 max-sm:max-h-none max-sm:flex-1"
      >
        {results.length === 0 ? (
          <li className="px-3 py-8 text-center text-sm text-muted-foreground">
            {loading ? 'Buscando…' : 'Nada encontrado.'}
          </li>
        ) : null}
        {results.map((r, i) => {
          const header = r.group !== lastGroup ? r.group : null;
          lastGroup = r.group;
          return (
            <li key={r.id} role="presentation">
              {header ? (
                <p className="px-2 pt-2 pb-1 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
                  {header}
                </p>
              ) : null}
              <div
                id={`gs-${i}`}
                role="option"
                aria-selected={i === current}
                data-index={i}
                onMouseMove={() => setActive(i)}
                onClick={() => go(r)}
                className={cn(
                  'flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-sm md:min-h-10',
                  i === current && 'bg-muted',
                )}
              >
                <span
                  className={cn(
                    'flex size-7 shrink-0 items-center justify-center rounded-md border bg-card text-muted-foreground',
                    i === current && 'border-primary/20 text-primary',
                  )}
                >
                  <r.icon className="size-4" aria-hidden />
                </span>
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium">{r.label}</span>
                  {r.detail ? (
                    <span className="ml-2 text-xs text-muted-foreground">{r.detail}</span>
                  ) : null}
                </span>
                {i === current ? (
                  <CornerDownLeft className="size-3.5 text-muted-foreground" aria-hidden />
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
